// A question a builder page asks in its own dialog, in place of the browser's OK/Cancel (as before discarding unsaved
// edits with Refresh).
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import React from 'react';
import { INTER, NAVY, SLATE } from '../style/verbaScholaStyles';

export interface ConfirmQuestion {
    title: string;
    message: string;
    /** The button that goes ahead (it discards something). */
    confirm: string;
    /** The button that goes back (default "Cancel"). */
    cancel?: string;
}

const buttonSx = { fontFamily: INTER, fontWeight: 700, textTransform: 'none', minHeight: 44 } as const;

/** ``ask`` resolves whether the teacher went ahead; ``dialog`` is rendered once on the page. */
export function useConfirmDialog() {
    const id = React.useId();
    const [question, setQuestion] = React.useState<(ConfirmQuestion & { resolve: (yes: boolean) => void }) | null>(null);
    // Kept while the dialog fades out, so its text does not vanish first.
    const shown = React.useRef<ConfirmQuestion | null>(null);
    if (question) shown.current = question;
    const ask = React.useCallback((next: ConfirmQuestion) => new Promise<boolean>(resolve => {
        setQuestion(current => { current?.resolve(false); return { ...next, resolve }; });
    }), []);
    const answer = (yes: boolean) => {
        question?.resolve(yes);
        setQuestion(null);
    };
    const dialog = <Dialog open={question !== null} onClose={() => answer(false)} aria-labelledby={`${id}-title`} maxWidth="xs" fullWidth>
        <DialogTitle id={`${id}-title`} sx={{ fontFamily: INTER, fontWeight: 700, color: NAVY }}>{shown.current?.title}</DialogTitle>
        <DialogContent>
            <Typography sx={{ fontFamily: INTER, fontSize: 14, color: NAVY }}>{shown.current?.message}</Typography>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
            <Button onClick={() => answer(false)} sx={{ ...buttonSx, color: SLATE }}>{shown.current?.cancel ?? 'Cancel'}</Button>
            <Button variant="contained" color="error" onClick={() => answer(true)} sx={buttonSx}>{shown.current?.confirm}</Button>
        </DialogActions>
    </Dialog>;
    return { dialog, ask };
}
